import {
  AssetType,
  Intervention,
  InterventionType,
  AssessmentMethodology,
  Outcome,
  MappedDropDownOptions,
} from "./types";
import { FormikValues } from "formik";
import { SolutionPayload } from "./hooks/useAddSolutionForm";
import { omitBy } from "lodash";

const hasTimestamp = (
  timestamp: number | string | null | undefined,
): timestamp is number | string =>
  timestamp !== null && timestamp !== undefined;

const parseTimestamp = (timestamp: number | string | null | undefined) => {
  if (!hasTimestamp(timestamp)) return null;

  const parsedTimestamp = parseInt(timestamp.toString());

  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
};

const SDGOptions = [
  {
    id: 1,
    displayValue: "No Poverty",
    color: "#e5233d",
  },
  {
    id: 2,
    displayValue: "Zero Hunger",
    color: "#dea73a",
  },
  {
    id: 3,
    displayValue: "Good Health & Wellbeing",
    color: "#4ca146",
  },
  {
    id: 4,
    displayValue: "Quality Education",
    color: "#c7212f",
  },
  {
    id: 5,
    displayValue: "Gender Equality",
    color: "#ef402d",
  },
  {
    id: 6,
    displayValue: "Clean Water And Sanitation",
    color: "#27bfe6",
  },
  {
    id: 7,
    displayValue: "Affordable And Clean Energy",
    color: "#fbc412",
  },
  {
    id: 8,
    displayValue: "Decent Work And Economic Growth",
    color: "#a31c44",
  },
  {
    id: 9,
    displayValue: "Industry, Innovation and Infrastructure",
    color: "#f26a2e",
  },
  {
    id: 10,
    displayValue: "Reduced Inequalities",
    color: "#e01483",
  },
  {
    id: 11,
    displayValue: "Sustainable Cities And Communities",
    color: "#f89d2a",
  },
  {
    id: 12,
    displayValue: "Responsible Consumption And Production",
    color: "#a57f38",
  },
  {
    id: 13,
    displayValue: "Climate Action",
    color: "#408046",
  },
  {
    id: 14,
    displayValue: "Life Below Water",
    color: "#1f97d4",
  },
  {
    id: 15,
    displayValue: "Life On Land",
    color: "#59ba47",
  },
  {
    id: 16,
    displayValue: "Peace, Justice And Strong Institutions",
    color: "#136a9f",
  },
  {
    id: 17,
    displayValue: "Partnerships For The Goals",
    color: "#14496b",
  },
];

export const interventionTypeOptions: Array<InterventionType> = [
  "Afforestation, Reforestation, and Revegetation",
  "Improved Agricultural Land Management",
  "Improved Land Management",
  "Avoided Deforestation",
  "Restoring and Protecting Wetland Ecosystems",
  "Biochar",
  "Improved Forest Management",
  "Habitat Restoration and Creation",
  "Tree Planting",
];

export const assetTypeOptions: Array<AssetType> = [
  "Tree Planting",
  "Biodiversity Credits",
  "Innovation",
  "Carbon Credits: Removal",
  "Carbon Credits: Avoidance",
  "Carbon Credits: Avoidance & Removal",
];

export const assessmentMethodologyOptions: Array<AssessmentMethodology> = [
  "KEYSTONE_2.0",
  "KEYSTONE_3.0",
];

export const mappedMethodologyField: Array<
  MappedDropDownOptions<AssessmentMethodology>
> = [
  { value: "KEYSTONE_2.0", label: "Keystone 2.0" },
  { value: "KEYSTONE_3.0", label: "Keystone 3.0" },
];

export const getSDGDisplayValue = (id: number) => {
  return SDGOptions.find((sdg) => sdg.id === id)?.displayValue;
};

export const getSDGChipColor = (id: number) => {
  return SDGOptions.find((sdg) => sdg.id === id)?.color;
};

export const outcomesTouched = (formValues: FormikValues) => {
  const { outcomes } = formValues;

  const touchedContainers = outcomes.find(
    (outcome: Outcome) =>
      outcome.value ||
      outcome.unit ||
      outcome["unit-of-sale"] ||
      outcome["outcome-start-date"] ||
      outcome["outcome-end-date"],
  );

  return !!touchedContainers;
};

export const isUnitOfSaleSelected = (formValues: FormikValues) => {
  const { outcomes } = formValues;

  const outComesWithUnitOfSaleSelected = outcomes.filter(
    (outcome: Outcome) => outcome["unit-of-sale"],
  );

  return !!outComesWithUnitOfSaleSelected.length;
};

export const isUnitOfSaleOutcomeFilledOut = (formValues: FormikValues) => {
  const { outcomes } = formValues;

  const unitOfSaleOutcomes = outcomes.filter(
    (outcome: Outcome) => outcome["unit-of-sale"],
  );

  return (
    unitOfSaleOutcomes.length > 0 &&
    unitOfSaleOutcomes.every(
      (outcome: Outcome) => !!(outcome.value && outcome.unit),
    )
  );
};

const normaliseInterventions = (obj: SolutionPayload) => {
  const interventions = obj.interventions?.reduce(
    (
      interventionsList: Array<Intervention>,
      singleIntervention: Intervention,
    ) => {
      const validEcosystemVaue = singleIntervention.action?.some(
        (action) => action?.trim() !== "",
      );
      const validActionValue = singleIntervention.ecosystem?.some(
        (ecosystem) => ecosystem?.trim() !== "",
      );

      if (validActionValue || validEcosystemVaue) {
        return interventionsList.concat([
          {
            action: singleIntervention.action,
            ecosystem: singleIntervention.ecosystem,
          },
        ]);
      }
      return interventionsList;
    },
    [],
  );

  return interventions;
};

const normaliseOutcomes = (payload: SolutionPayload) => {
  const outcomes = payload.outcomes?.reduce(
    (
      outcomesList: Array<{
        unit: string;
        value: number;
        isUnitOfSale: boolean;
        startDate?: number;
        endDate?: number;
      }>,
      singleOutcome,
    ) => {
      if (singleOutcome.unit && singleOutcome.value) {
        const hasStartDate =
          singleOutcome.startDate !== null &&
          singleOutcome.startDate !== undefined;
        const hasEndDate =
          singleOutcome.endDate !== null && singleOutcome.endDate !== undefined;

        return outcomesList.concat([
          {
            unit: singleOutcome.unit,
            value:
              typeof singleOutcome.value !== "number"
                ? parseFloat(singleOutcome.value)
                : singleOutcome.value,
            isUnitOfSale: singleOutcome.isUnitOfSale,
            ...(hasStartDate && {
              startDate: Number(singleOutcome.startDate),
            }),
            ...(hasEndDate && {
              endDate: Number(singleOutcome.endDate),
            }),
          },
        ]);
      }
      return outcomesList;
    },
    [],
  );
  return outcomes;
};

export const normaliseToStringArray = (
  value: string | Array<string> | null | undefined,
): Array<string> => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return [value];
  return [];
};

export function removeNonValues(payload: SolutionPayload) {
  const date = omitBy(payload.date, (field) => !field);

  const coordinates =
    payload.coordinates?.latitude && payload.coordinates?.longitude
      ? payload.coordinates
      : null;

  const interventions = normaliseInterventions(payload);

  const outcomes = normaliseOutcomes(payload);

  const programmeInfo = Object.fromEntries(
    Object.entries(payload.programmeInfo || {}).filter(
      ([_, value]) =>
        (typeof value === "string" && value) ||
        (Array.isArray(value) && value.length),
    ),
  );

  const internalUseOnly = Object.fromEntries(
    Object.entries(payload.internalUseOnly || {}).filter(([_, value]) => value),
  );
  const nestedNonValuesRemoved = {
    ...payload,
    coordinates,
    date,
    programmeInfo,
    internalUseOnly,
  };

  const omitTopLevel = omitBy(
    nestedNonValuesRemoved,
    (field) =>
      !field || (typeof field === "object" && Object.keys(field).length === 0),
  );

  return {
    ...omitTopLevel,
    interventions,
    outcomes,
  };
}

export function createUpdateSolutionForm(form: FormikValues) {
  const payload: SolutionPayload = {
    name: form["solution-name"],
    vbcWebsiteDisplayName: form["solution-vbc-website-display-name"] || null,
    developer: form["solution-developer"] || null,
    shortDescription: form["solution-short-description"] || null,
    country: form["country-select"] || [],
    coordinates:
      form["latitude"] && form["longitude"]
        ? {
            latitude: parseFloat(form["latitude"]),
            longitude: parseFloat(form["longitude"]),
          }
        : null,
    developmentStage: form["solution-development-stage"] || [],
    assessmentMethodology: form["solution-assessment-methodology"] || null,
    interventionType: form["solution-intervention-type"] || null,
    SDG: form["solution-sdg"],
    date: {
      start: parseTimestamp(form["solution-start-date"]),
      end: parseTimestamp(form["solution-end-date"]),
    },
    isReadyForSale: form["solution-is-ready-for-sale"],
    isUsedForCarbonOffsetting: form["solution-is-carbon-offset"],
    interventions: form["interventions"].map((intervention: Intervention) => ({
      action: intervention.action,
      ecosystem: intervention.ecosystem,
    })),
    outcomes: form["outcomes"].map((outcome: Outcome) => ({
      value: parseFloat(outcome.value),
      unit: outcome.unit,
      isUnitOfSale: outcome["unit-of-sale"],
      startDate: parseTimestamp(outcome["outcome-start-date"]),
      endDate: parseTimestamp(outcome["outcome-end-date"]),
    })),
    programmeInfo: {
      carbonCreditProgramme:
        form["programme-info"]["carbon-crediting-programme"],
      projectDescriptionDocument:
        form["programme-info"]["description-document"] || null,
      accountingMethodology:
        form["programme-info"]["carbon-accounting-methodology"] || null,
      assetType: form["programme-info"]["asset-type"] || null,
    },
    internalUseOnly: {
      websiteUrl: form["internal-details"]["website-url"] || null,
      xeroAccountNumber:
        form["internal-details"]["xero-account-number"] || null,
      researchTeamNotes:
        form["internal-details"]["research-team-notes"] || null,
      additionalResourceLink:
        form["internal-details"]["additional-resources-link"] || null,
    },
    riskMonitoring: {
      isEnabled: form["risk-monitoring"]["is-risk-alert-enabled"] || false,
      buffer: form["risk-monitoring"]["buffer-value-km"] || null,
      geospatialFile: form["risk-monitoring"]["geospatial-file"] || null,
    },
  };

  const interventions = normaliseInterventions(payload);

  const outcomes = normaliseOutcomes(payload);

  return {
    ...payload,
    interventions: interventions || [],
    outcomes: outcomes || [],
  };
}

export default {
  SDG: {
    SDGOptions: Object.freeze(SDGOptions),
    getSDGDisplayValue,
    getSDGChipColor,
  },
  SolutionFormBuilder: {
    removeNonValues,
    updateSolution: createUpdateSolutionForm,
  },
};
